import React, { useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CollapseHorizontalIcon from '@atlaskit/icon/core/collapse-horizontal';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

export type Side = 'left' | 'right' | null;

interface SideBySideDividerProps {
	expandedSide: Side;
	onExpandSide: (side: Side) => void;
}

const styles = cssMap({
	container: {
		display: 'flex',
		alignSelf: 'stretch',
		flexShrink: 0,
		paddingBlock: token('space.400'),
	},
	clickArea: {
		width: '16px',
		backgroundColor: 'transparent',
		padding: '0',
	},
	indicator: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.025'),
		boxShadow: token('elevation.shadow.overflow'),
		zIndex: 1,
		pointerEvents: 'none',
	},
	divider: {
		position: 'relative',
		flexShrink: 0,
		alignSelf: 'stretch',
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
	},
});

export function SideBySideDivider({
	expandedSide,
	onExpandSide,
}: SideBySideDividerProps): JSX.Element {
	const [hoveredSide, setHoveredSide] = useState<Side>(null);

	const handleLeftClick = useCallback(() => {
		onExpandSide(expandedSide ? null : 'left');
	}, [expandedSide, onExpandSide]);

	const handleRightClick = useCallback(() => {
		onExpandSide(expandedSide ? null : 'right');
	}, [expandedSide, onExpandSide]);

	const isExpanded = expandedSide !== null;

	return (
		<Box xcss={styles.container}>
			<Pressable
				xcss={styles.clickArea}
				onClick={handleLeftClick}
				onMouseEnter={() => setHoveredSide('left')}
				onMouseLeave={() => setHoveredSide((current) => (current === 'left' ? null : current))}
				aria-label={isExpanded ? 'Reset to side-by-side' : 'Expand left panel'}
			/>
			<Box xcss={styles.divider}>
				{hoveredSide && (
					<Box xcss={styles.indicator}>
						{isExpanded ? (
							<CollapseHorizontalIcon label="" color={token('color.icon.subtle')} size="small" />
						) : hoveredSide === 'left' ? (
							<ChevronLeftIcon label="" color={token('color.icon.subtle')} size="small" />
						) : (
							<ChevronRightIcon label="" color={token('color.icon.subtle')} size="small" />
						)}
					</Box>
				)}
			</Box>
			<Pressable
				xcss={styles.clickArea}
				onClick={handleRightClick}
				onMouseEnter={() => setHoveredSide('right')}
				onMouseLeave={() => setHoveredSide((current) => (current === 'right' ? null : current))}
				aria-label={isExpanded ? 'Reset to side-by-side' : 'Expand right panel'}
			/>
		</Box>
	);
}
